import { useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  subYears,
  parseISO,
} from 'date-fns';

import type { DateRange } from '@/types/comparison';
import { DateField } from '@/components/forms/DateField';

type Preset = 'same-month' | 'pre-rut' | 'custom';

interface PeriodPickerProps {
  onPeriodsChange: (periodA: DateRange, periodB: DateRange) => void;
}

function toDateString(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function formatRangeLabel(range: DateRange): string {
  const start = parseISO(range.start);
  const end = parseISO(range.end);
  if (format(start, 'MMM yyyy') === format(end, 'MMM yyyy')) {
    return `${format(start, 'MMM d')}–${format(end, 'MMM d, yyyy')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

function computeDefaults(): { periodA: DateRange; periodB: DateRange } {
  const now = new Date();
  const periodBStart = startOfMonth(now);
  const periodBEnd = endOfMonth(now);
  const periodAStart = startOfMonth(subYears(now, 1));
  const periodAEnd = endOfMonth(subYears(now, 1));
  return {
    periodA: { start: toDateString(periodAStart), end: toDateString(periodAEnd) },
    periodB: { start: toDateString(periodBStart), end: toDateString(periodBEnd) },
  };
}

function buildSameMonthPreset(): { periodA: DateRange; periodB: DateRange } {
  return computeDefaults();
}

function buildPreRutPreset(): { periodA: DateRange; periodB: DateRange } {
  const now = new Date();
  const thisYear = now.getFullYear();
  const lastYear = thisYear - 1;
  return {
    periodA: { start: `${lastYear}-10-15`, end: `${lastYear}-11-01` },
    periodB: { start: `${thisYear}-10-15`, end: `${thisYear}-11-01` },
  };
}

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'same-month', label: 'Same month, last year vs. this year' },
  { id: 'pre-rut', label: 'Pre-rut (Oct 15 – Nov 1)' },
  { id: 'custom', label: 'Custom' },
];

export function PeriodPicker({ onPeriodsChange }: PeriodPickerProps) {
  const defaults = computeDefaults();
  const [activePreset, setActivePreset] = useState<Preset>('same-month');
  const [periodA, setPeriodA] = useState<DateRange>(defaults.periodA);
  const [periodB, setPeriodB] = useState<DateRange>(defaults.periodB);

  function applyPreset(preset: Preset) {
    setActivePreset(preset);
    if (preset === 'same-month') {
      const { periodA: a, periodB: b } = buildSameMonthPreset();
      setPeriodA(a);
      setPeriodB(b);
    } else if (preset === 'pre-rut') {
      const { periodA: a, periodB: b } = buildPreRutPreset();
      setPeriodA(a);
      setPeriodB(b);
    }
    // 'custom' — keep existing values
  }

  function handleApply() {
    onPeriodsChange(periodA, periodB);
  }

  const labelClass =
    'field-label block mb-1';

  return (
    <div className="flex flex-col gap-4" data-testid="period-picker">
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => applyPreset(id)}
            data-testid={`preset-${id}`}
            className={`px-3 py-1.5 rounded-md text-body-xs font-medium border transition-colors ${
              activePreset === id
                ? 'bg-[var(--color-accent-hover)] border-[var(--color-accent)] text-[var(--color-text-primary)]'
                : 'bg-transparent border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Date range inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Period A */}
        <div
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3 flex flex-col gap-2"
          data-testid="period-a-panel"
        >
          <span className={labelClass}>Period A</span>
          <div className="flex flex-col gap-1.5 min-w-0">
            <DateField
              id="period-a-start"
              label="From"
              value={periodA.start}
              onChange={(v) => {
                const updated = { ...periodA, start: v };
                setPeriodA(updated);
                setActivePreset('custom');
              }}
            />
            <DateField
              id="period-a-end"
              label="To"
              value={periodA.end}
              onChange={(v) => {
                const updated = { ...periodA, end: v };
                setPeriodA(updated);
                setActivePreset('custom');
              }}
            />
          </div>
          {periodA.start && periodA.end && (
            <p className="text-body-xs text-[var(--color-accent)]" data-testid="period-a-label">
              {formatRangeLabel(periodA)}
            </p>
          )}
        </div>

        {/* Period B */}
        <div
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3 flex flex-col gap-2"
          data-testid="period-b-panel"
        >
          <span className={labelClass}>Period B</span>
          <div className="flex flex-col gap-1.5 min-w-0">
            <DateField
              id="period-b-start"
              label="From"
              value={periodB.start}
              onChange={(v) => {
                const updated = { ...periodB, start: v };
                setPeriodB(updated);
                setActivePreset('custom');
              }}
            />
            <DateField
              id="period-b-end"
              label="To"
              value={periodB.end}
              onChange={(v) => {
                const updated = { ...periodB, end: v };
                setPeriodB(updated);
                setActivePreset('custom');
              }}
            />
          </div>
          {periodB.start && periodB.end && (
            <p className="text-body-xs text-[var(--color-accent)]" data-testid="period-b-label">
              {formatRangeLabel(periodB)}
            </p>
          )}
        </div>
      </div>

      {/* Apply */}
      <button
        type="button"
        onClick={handleApply}
        data-testid="apply-button"
        className="self-start px-4 py-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-[var(--color-text-primary)] rounded-md text-body-sm font-medium transition-colors"
      >
        Compare
      </button>
    </div>
  );
}
